import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Hooks } from "@xt/xtbase";
import step2Img from "@/assets/img/cfd-guide/guide1.png";
import lineImg from "@/assets/img/cfd-guide/line.png";
import addIcon from "@/assets/img/add.png";
import store from "store";
import { useCfdGuideStageRect } from "./useCfdGuideStageRect";
import styles from "./StandardGuideStep2.module.scss";

const { useTranslation } = Hooks;

export type StandardGuideStep2Props = {
  onSkip: () => void;
  onNext: () => void;
};

const DESIGN_WIDTH = 375;

function getScale(): number {
  if (typeof window === "undefined") return 1;
  const w = window.innerWidth || DESIGN_WIDTH;
  return w / DESIGN_WIDTH;
}

const StandardGuideStep2: React.FC<StandardGuideStep2Props> = ({ onSkip, onNext }) => {
  const t = useTranslation();
  const { name } = store.market;

  const scale = useMemo(() => getScale(), []);

  // 获取货币显示名称
  const coinPriceUpperCase = useMemo(() => {
    if (!name) return "USDT";
    const parts = name.split("_");
    return parts[1]?.toUpperCase() || "USDT";
  }, [name]);

  // 货币简写映射
  const currencyAbbreviationMap: Record<string, string> = {
    USDT: "U",
    AED: "AED",
    BRL: "BRL",
    SAR: "SAR",
  };

  // 获取货币显示名称（优先使用简写）
  const currencyDisplayName = useMemo(() => {
    return currencyAbbreviationMap[coinPriceUpperCase] || coinPriceUpperCase;
  }, [coinPriceUpperCase]);

  // 快速选择保证金选项
  const quickMarginOptions = useMemo(() => {
    const amounts = ["20", "50", "100", "200"];
    return amounts.map((amount) => `${amount}${currencyDisplayName}`);
  }, [currencyDisplayName]);

  const imgSrc = (step2Img as any)?.src || (step2Img as any);
  const lineImgSrc = (lineImg as any)?.src || (lineImg as any);
  const addIconSrc = (addIcon as any)?.src || (addIcon as any);

  const anchor = useCfdGuideStageRect('[data-guide-target="marginSection"]');

  return (
    <div style={{ ["--sg-scale" as any]: scale }}>
        {/* 高亮 + 提示框同一容器，位置与真实保证金区域对齐 */}
        <div
          className={styles.highlightAndTipWrapper}
          style={
            anchor
              ? {
                  position: "fixed",
                  top: anchor.top,
                  left: anchor.left,
                  width: anchor.width,
                  zIndex: 10001,
                }
              : { visibility: "hidden" as const }
          }
        >
          {/* 高亮区域（保证金输入区域） */}
          <div className={styles.highlightRect} style={anchor ? { minHeight: anchor.height } : undefined}>
            {/* 高亮框内的保证金输入区域结构 */}
            <div className={styles.marginSection}>
              <div className={styles.marginHeader}>
                <span className={styles.label}>{t("cfd.margin")}</span>
                <div className={styles.available}>
                  <span>{t("cfd.available")}: 2000 {coinPriceUpperCase}</span>
                  <button className={styles.addBtn}>
                    <img src={addIconSrc} alt="add" />
                  </button>
                </div>
              </div>
              <div className={styles.marginInput}>
                <input type="text" placeholder={t("cfd.pleaseEnter")} readOnly />
              </div>
              <div className={styles.quickOptions}>
                {quickMarginOptions.map((option, index) => (
                  <button key={option} className={`${styles.quickBtn} ${index === 0 ? styles.active : ""}`}>
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 引导提示框：紧贴高亮下方，不依赖固定 top/100vh */}
          <div className={styles.tip}>
          <div className={styles.tipBg}>
            <div className={styles.tipText}>
              <div className={styles.tipTitle}>{t("cfd.guide.standard.step2.title")}</div>
              <div className={styles.tipDesc}>{t("cfd.guide.standard.step2.desc")}</div>
            </div>
            <img className={styles.tipImg} src={imgSrc} alt="" />
          </div>
          <div className={styles.star1} />
          <div className={styles.star2} />
          <img className={styles.lineImg} src={lineImgSrc} alt="" />
        </div>
        </div>

        {/* 底部按钮 */}
        <div className={styles.btnGroup}>
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onNext}>
            {`${t("cfd.guide.next")} 2/7`}
          </button>
          <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onSkip}>
            {t("cfd.guide.skip")}
          </button>
      </div>
    </div>
  );
};

export default observer(StandardGuideStep2);
